import {
  FormControl,
  FormHelperText,
  FormLabel,
  InputProps,
  Skeleton,
} from '@mui/joy'
import { ParseKeys } from 'i18next'
import React from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { NumericFormat } from 'react-number-format'
import { NumericFormatProps } from 'react-number-format/types/types'
import { useParserI18nMessage } from '~/shared/lib/useParserI18nMessage'
import { Input } from '../ui/styled'

type NumericInputProps = {
  label?: ParseKeys
  placeholder?: ParseKeys
  name: string
  skeletonShow?: boolean
  numericProps?: NumericFormatProps
} & Omit<InputProps, 'name' | 'slotProps'>

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void
  params: {
    numericProps?: NumericFormatProps
  }
}

const NumericAdapter = React.forwardRef<NumericFormatProps, CustomProps>(
  function NumericFormatAdapter({ params, ...other }, ref) {
    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        thousandSeparator=' '
        allowNegative={false}
        decimalScale={2}
        decimalSeparator={','}
        {...params?.numericProps}
      />
    )
  },
)

export function NumericInput({
  label,
  placeholder,
  name,
  skeletonShow,
  numericProps,
  ...inputProps
}: NumericInputProps) {
  const { t } = useTranslation()
  const { parserI18nMessage } = useParserI18nMessage()
  return (
    <Controller
      name={name}
      render={({ field, fieldState: { error, invalid } }) => (
        <FormControl error={invalid}>
          {label && <FormLabel>{t(label)}</FormLabel>}

          <Input
            fullWidth
            size='lg'
            {...inputProps}
            {...field}
            {...(placeholder ? { placeholder: t(placeholder) } : {})}
            error={invalid}
            slotProps={{
              ...(skeletonShow ? { root: { component: Skeleton } } : {}),
              input: {
                component: NumericAdapter,
                params: {
                  numericProps,
                },
              },
            }}
          />
          {invalid && (
            <FormHelperText data-testid={`error-message-${label && t(label)}`}>
              {parserI18nMessage(error?.message)}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  )
}
